/* still.hobbies — smart card-handle search ergonomics.

   Vanilla predictive search tokenizes title + tags. Tags include the
   card number with its native zero-padding (e.g., "001"), so to find
   PFL #001 the user has to type the EXACT padded number. Most people
   typing fast write "pfl 1" or "pfl-1" or "pfl 01" — none of which
   prefix-match "001". This module fixes that by normalizing the
   query before predictive-search fires AND before the form submits,
   plus surfaces a "quick jump" link to the canonical product page
   when the input is unambiguously a (set, number) pair.

   Patterns recognized:
     pfl-001        → set=pfl, num=001
     pfl 001        → set=pfl, num=001
     pfl-1          → set=pfl, num=001 (zero-padded to 3 digits)
     pfl 1          → set=pfl, num=001
     pfl 01         → set=pfl, num=001
     pfl-001-holo   → set=pfl, num=001, finish=holo (jumps to per-finish handle)
     swsh09:tg-1    → set=swsh09-tg, num=001 (set codes with `:` get the
                       same normalization as catalog_json.safe_filename_set_code)

   Free-form queries that don't match the pattern (e.g., "charizard",
   "pikachu", "rare candy") fall through unchanged — Shopify's
   predictive search handles them as before.

   Auto-binds to every <predictive-search> form on the page (header
   inline search + hero homepage search + standalone /search page).
*/

(function() {
    'use strict';

    /* Normalize a set code the same way the inventory app's
       catalog_json.safe_filename_set_code does:
         - lowercase
         - non-[a-z0-9-] → '-'
         - collapse repeated dashes
         - strip leading/trailing dashes
       Mirrors `inventory.make_parent_handle` so the resulting URL
       slug always lines up with what the sync push creates. */
    function normSetCode(s) {
        return String(s || '')
            .toLowerCase()
            .replace(/[^a-z0-9-]/g, '-')
            .replace(/-+/g, '-')
            .replace(/^-|-$/g, '');
    }

    /* Try to interpret the query as a (set, number, optional finish)
       triple. Returns null when it doesn't look like a card handle.
       Examples:
         "pfl-001"      → {setCode:"pfl", num:"001", finish:""}
         "pfl 1"        → {setCode:"pfl", num:"001", finish:""}
         "pfl-001-holo" → {setCode:"pfl", num:"001", finish:"holo"}
         "swsh09:tg-1"  → {setCode:"swsh09-tg", num:"001", finish:""}
         "charizard"    → null
         "001"          → null  (need a set code too)
    */
    function parseCardHandle(q) {
        if (!q) return null;
        const trimmed = String(q).trim();
        // Set code: 2-12 chars of [a-z0-9:_]+ (allow Shopify-rejected
        // chars in the input — we'll normalize them out below).
        // Number:   1-4 digits, optionally with a leading 0
        // Finish:   optional word/dashy suffix
        const m = trimmed.match(
            /^([A-Za-z0-9:_]{2,12})[\s\-_]+(\d{1,4})(?:[\s\-_]+([A-Za-z][A-Za-z0-9\s\-_()]*))?$/
        );
        if (!m) return null;
        const [, setRaw, numRaw, finishRaw] = m;
        const setCode = normSetCode(setRaw);
        if (!setCode) return null;
        // Zero-pad numbers to 3 digits — matches catalog convention.
        const num = numRaw.padStart(3, '0');
        const finish = (finishRaw || '')
            .toLowerCase().trim()
            .replace(/[\s_]+/g, '-')
            .replace(/[()]/g, '')
            .replace(/-+/g, '-')
            .replace(/^-|-$/g, '');
        return { setCode, num, finish };
    }

    /* Build the search-friendly token form: "<set> <num>". Predictive
       search tokenizes on whitespace and runs an AND match against
       title + tags. Tags include both the set code (PFL) and the
       card number ("001"), so this token form reliably hits the
       right products. */
    function toSearchTokens(parsed) {
        if (!parsed) return null;
        return parsed.finish
            ? `${parsed.setCode} ${parsed.num} ${parsed.finish}`
            : `${parsed.setCode} ${parsed.num}`;
    }

    /* Build the canonical Shopify product handle. */
    function toProductHandle(parsed) {
        if (!parsed) return null;
        return parsed.finish
            ? `${parsed.setCode}-${parsed.num}-${parsed.finish}`
            : `${parsed.setCode}-${parsed.num}`;
    }

    /* Render (or update / hide) a "Quick jump" pill above the
       predictive-search dropdown for `host`. When the input matches
       a card-handle pattern, the pill links to the canonical product
       page. */
    function renderQuickJump(host, input, parsed) {
        const resultsPanel = host.querySelector('[data-predictive-search]');
        if (!resultsPanel) return;
        let pill = host.querySelector('.still-quick-jump');
        if (!parsed) {
            if (pill) pill.remove();
            return;
        }
        const handle = toProductHandle(parsed);
        const label = parsed.finish
            ? `Jump to ${parsed.setCode.toUpperCase()} #${parsed.num} (${parsed.finish.replace(/-/g, ' ')})`
            : `Jump to ${parsed.setCode.toUpperCase()} #${parsed.num}`;
        if (!pill) {
            pill = document.createElement('a');
            pill.className = 'still-quick-jump';
            pill.setAttribute('role', 'option');
            // Inline styles keep this self-contained — no extra CSS file.
            pill.style.cssText = [
                'display: flex', 'align-items: center', 'gap: 8px',
                'padding: 10px 14px',
                'border: 1px solid var(--color-still-gold, #c9a14d)',
                'border-radius: 4px',
                'margin: 8px 12px 4px',
                'font-family: var(--font-still-mono, monospace)',
                'font-size: 12px',
                'letter-spacing: 0.04em',
                'color: var(--color-still-gold, #c9a14d)',
                'text-decoration: none',
                'background: rgba(201, 161, 77, 0.06)',
            ].join(';');
            pill.innerHTML =
                '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">' +
                '<path d="M3 7h8m0 0L7 3m4 4l-4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
                '</svg><span class="still-quick-jump__label"></span>';
            resultsPanel.parentNode.insertBefore(pill, resultsPanel);
        }
        pill.href = `/products/${handle}`;
        pill.querySelector('.still-quick-jump__label').textContent = label;
    }

    function hookForm(host) {
        if (host.dataset.stillCardSearchHooked === '1') return;
        host.dataset.stillCardSearchHooked = '1';
        const form = host.querySelector('form[role="search"]');
        const input = host.querySelector('input[type="search"][name="q"]');
        if (!form || !input) return;

        // Track latest-parsed state so submit can use it without re-parsing.
        let lastParsed = null;

        input.addEventListener('input', () => {
            lastParsed = parseCardHandle(input.value);
            renderQuickJump(host, input, lastParsed);
        });

        // On submit: if the input parses as a card-handle pattern,
        // jump straight to the product page (canonical handle). The
        // /products/<handle> URL 404s gracefully if the product
        // doesn't exist; keep the original /search?q=... fallback by
        // probing the product first.
        form.addEventListener('submit', async (e) => {
            const parsed = parseCardHandle(input.value);
            if (!parsed) return;        // free-form query → normal /search
            e.preventDefault();
            const handle = toProductHandle(parsed);

            // Probe /products/<handle>.js — Shopify returns 200 with
            // product JSON if it exists, 404 otherwise. Fast (~50ms).
            try {
                const r = await fetch(`/products/${handle}.js`, {
                    method: 'GET',
                    headers: { 'Accept': 'application/json' },
                    credentials: 'omit',
                });
                if (r.ok) {
                    window.location.assign(`/products/${handle}`);
                    return;
                }
                // 404 — try the parent (without finish) before falling
                // back to /search. e.g., "pfl-001-holo" not found →
                // "pfl-001" parent might exist with a Holo variant.
                if (parsed.finish) {
                    const parentHandle = `${parsed.setCode}-${parsed.num}`;
                    const r2 = await fetch(`/products/${parentHandle}.js`, {
                        method: 'GET',
                        headers: { 'Accept': 'application/json' },
                        credentials: 'omit',
                    });
                    if (r2.ok) {
                        window.location.assign(`/products/${parentHandle}`);
                        return;
                    }
                }
            } catch (_) {
                // Network error — fall through to /search.
            }

            // Fallback: rewrite the query to space-separated tokens
            // (zero-padded number) so /search hits the right tags
            // even though the direct product page doesn't exist.
            input.value = toSearchTokens(parsed);
            form.submit();
        });
    }

    function bindAll() {
        document.querySelectorAll('predictive-search').forEach(hookForm);
        // Also hook plain (non-predictive-search) forms in the same
        // shape — the homepage hero and header both fall back to a
        // bare <form> when settings.predictive_search_enabled is off.
        document.querySelectorAll('form[role="search"][action$="/search"]').forEach(form => {
            const host = form.closest('predictive-search') || form;
            if (!host.dataset.stillCardSearchHooked) hookForm(host);
        });
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', bindAll);
    } else {
        bindAll();
    }
    // Re-bind after Section Rendering API swaps. Shopify dispatches
    // 'shopify:section:load' on dynamic section reload.
    document.addEventListener('shopify:section:load', bindAll);
})();
